import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { brandPrimary, charmGray, error, orange, white } from '../../../../web/styles/colors';

export const styleSheet = StyleSheet.create({
	container: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		padding: '16px',
		backgroundColor: white,
		overflow: 'hidden',
	},
	header: {
		borderBottom: `1px solid ${charmGray}`,
		paddingBottom: '12px',
		marginBottom: '16px',
	},
	headerWithBack: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '8px',
	},
	backButton: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		padding: 0,
		border: 'none',
		borderRadius: '6px',
		backgroundColor: 'transparent',
		color: brandPrimary,
		cursor: 'pointer',
		flexShrink: 0,
		':hover': {
			backgroundColor: 'rgba(0, 170, 232, 0.1)',
		},
	},
	title: {
		fontSize: '18px',
		fontWeight: 600,
		color: brandPrimary,
		margin: 0,
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	},
	subtitle: {
		fontSize: '14px',
		color: charmGray,
		margin: '4px 0 0 0',
	},
	coachTemplate: {
		...THEME.common.truncate,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		margin: 0,
		marginTop: 4,
	},
	messagesContainer: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
	},
	messagesContainerHalf: {
		flex: '0 1 50%',
	},
	// Assistant (AI Coach) message styles
	assistantMessage: {
		padding: '12px',
		borderRadius: '8px',
		backgroundColor: '#f5f5f5',
		border: '1px solid #e0e0e0',
	},
	assistantMessageLabel: {
		fontSize: '12px',
		fontWeight: 600,
		color: brandPrimary,
		textTransform: 'uppercase',
	},
	// User (Transcript Sent) message styles
	userMessage: {
		backgroundColor: '#f0f4f8',
		borderRadius: 8,
		padding: 12,
		marginBottom: 8,
		marginLeft: 24,
	},
	userMessageLabel: {
		fontSize: 11,
		fontWeight: 600,
		color: '#6b7280',
		marginBottom: 4,
		textTransform: 'uppercase' as const,
	},
	// System message styles (visual separator between call sessions)
	systemMessage: {
		padding: '8px 12px',
		textAlign: 'center' as const,
		borderTop: '1px solid #e0e0e0',
		borderBottom: '1px solid #e0e0e0',
		backgroundColor: '#fafafa',
	},
	systemMessageText: {
		fontSize: '12px',
		color: charmGray,
		fontStyle: 'italic',
	},
	// Legacy messageCard (keeping for historical messages view)
	messageCard: {
		padding: '12px',
		borderRadius: '8px',
		backgroundColor: '#f5f5f5',
		border: '1px solid #e0e0e0',
	},
	messageHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: '8px',
	},
	messageLabel: {
		fontSize: '12px',
		fontWeight: 600,
		color: brandPrimary,
		textTransform: 'uppercase',
	},
	messageTimestamp: {
		fontSize: '11px',
		color: charmGray,
	},
	messageContent: {
		fontSize: '14px',
		lineHeight: '1.5',
		color: '#333',
	},
	streamingIndicator: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		fontSize: '11px',
		color: brandPrimary,
		marginLeft: '8px',
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		color: charmGray,
		textAlign: 'center',
		padding: '24px',
	},
	emptyStateIcon: {
		fontSize: '48px',
		marginBottom: '16px',
	},
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '8px',
		padding: '24px',
		color: charmGray,
	},
	errorMessage: {
		padding: '12px',
		borderRadius: '8px',
		backgroundColor: '#fff3f3',
		border: `1px solid ${error}`,
		color: error,
		fontSize: '14px',
	},
	// Button container
	buttonContainer: {
		padding: '12px',
		borderTop: `1px solid ${charmGray}`,
		backgroundColor: white,
		marginTop: 'auto',
	},
	getSuggestionButton: {
		width: '100%',
		padding: '10px 16px',
		fontSize: '14px',
		fontWeight: 600,
		color: white,
		backgroundColor: brandPrimary,
		border: 'none',
		borderRadius: '6px',
		cursor: 'pointer',
		transition: 'background-color 0.2s',
		':hover': {
			backgroundColor: '#0095c8',
		},
		':disabled': {
			backgroundColor: charmGray,
			cursor: 'not-allowed',
		},
	},
	// Connection status indicator
	connectionBulb: {
		display: 'inline-block',
		width: '12px',
		height: '12px',
		borderRadius: '50%',
		flexShrink: 0,
		cursor: 'default',
	},
	connectionBulbConnected: {
		backgroundColor: '#22c55e', // green-500
		boxShadow: '0 0 0 2px rgba(34,197,94,0.25)',
	},
	connectionBulbDisconnected: {
		backgroundColor: '#ef4444', // red-500
		boxShadow: '0 0 0 2px rgba(239,68,68,0.25)',
	},

	// ── Latest Coaching Card ─────────────────────────────────────────────────
	latestCoachingCard: {
		border: `2px solid ${brandPrimary}`,
		borderRadius: '10px',
		padding: '16px',
		backgroundColor: white,
		marginBottom: '8px',
	},
	latestCoachingHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
		fontSize: '11px',
		fontWeight: 700,
		color: brandPrimary,
		textTransform: 'uppercase' as const,
		letterSpacing: '0.6px',
		marginBottom: '12px',
	},

	// ── SAY THIS bubble ──────────────────────────────────────────────────────
	sayThisBubble: {
		backgroundColor: '#dbeafe',
		borderRadius: '10px',
		padding: '14px 16px',
		marginBottom: '12px',
	},
	sayThisLabel: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
		fontSize: '11px',
		fontWeight: 700,
		color: brandPrimary,
		textTransform: 'uppercase' as const,
		letterSpacing: '0.6px',
		marginBottom: '8px',
	},
	sayThisText: {
		fontSize: '14px',
		color: '#111827',
		lineHeight: '1.6',
		margin: 0,
	},

	// ── Reasoning section ────────────────────────────────────────────────────
	reasoningRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: '8px',
	},
	reasoningIcon: {
		fontSize: '14px',
		flexShrink: 0,
		marginTop: '1px',
	},
	reasoningText: {
		fontSize: '12px',
		color: '#6b7280',
		lineHeight: '1.5',
		margin: 0,
	},

	// ── History toggle ───────────────────────────────────────────────────────
	historyToggleButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		fontSize: '13px',
		color: charmGray,
		padding: '8px 0',
		textAlign: 'center' as const,
		width: '100%',
		':hover': {
			color: brandPrimary,
		},
	},

	// ── Sticky Game Plan Card ───────────────────────────────────────────────
	gamePlanCard: {
		border: `2px solid ${orange}`,
		borderRadius: '10px',
		padding: '12px 16px',
		backgroundColor: white,
		flexShrink: 0,
	},
	gamePlanCardExpanded: {
		flex: '0 1 50%',
		overflowY: 'auto',
		minHeight: 0,
	},
	gamePlanToggle: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		color: orange,
		fontSize: '12px',
	},
	gamePlanHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: '6px',
		fontSize: '11px',
		fontWeight: 700,
		color: orange,
		textTransform: 'uppercase' as const,
		letterSpacing: '0.6px',
	},
});
